import type { Configuration } from '@azure/msal-browser';

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

import { AZURE_AD } from 'src/global-config';

import dayjs from './dayjs';

const msalConfig: Configuration = {
  auth: {
    clientId: AZURE_AD.clientId,
    authority: AZURE_AD.authority,
    postLogoutRedirectUri: '/',
    redirectUri: '/', // Resolve the redirectUri issue
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);
export const scopes = ['openid', 'profile', 'User.Read'];

const initializeMsal = async (): Promise<void> => {
  await msalInstance.initialize();

  try {
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    console.error('Error handling redirect:', error);
  }
};

// Initialize MSAL to avoid empty accounts
initializeMsal();

export const getAuthToken = async (): Promise<string | undefined> => {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    return undefined;
  }

  const account = accounts[0];
  const idTokenClaims = account.idTokenClaims;

  let needsRefresh = false;

  if (idTokenClaims?.exp) {
    const expiresOn = dayjs.unix(idTokenClaims.exp);
    const refreshTime = dayjs().add(15, 'minute'); // 15 minutes from now

    // If the token expires in the next 15 minutes, it needs a refresh.
    if (expiresOn.isBefore(refreshTime)) {
      needsRefresh = true;
    }
  } else {
    // If there's no expiration claim, assume it needs a refresh to be safe.
    needsRefresh = true;
  }

  // If a refresh isn't needed and we have an idToken, return it.
  if (!needsRefresh && account.idToken) {
    return account.idToken;
  }

  const accessTokenRequest = {
    scopes: [], // empty scope for idToken
    account,
  };

  // Otherwise, acquire a new token silently.
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...accessTokenRequest,
      forceRefresh: needsRefresh,
    });

    return response.idToken;
  } catch (error) {
    console.error('Error retrieving token silently:', error);

    // Fallback to interactive method if silent fails
    if (error instanceof InteractionRequiredAuthError) {
      console.log('Silent token acquisition failed. Falling back to popup.');
      try {
        const response = await msalInstance.acquireTokenPopup(accessTokenRequest);
        return response.idToken;
      } catch (popupError) {
        console.error('Error acquiring token with popup:', popupError);
        throw new Error('Failed to retrieve token interactively');
      }
    }

    throw new Error('Failed to retrieve token');
  }
};
